import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

/**
 * Element Plus
 * Import element-plus/index.scss before scss of element-plus
 * to avoid the problem of sass mixed variables
 */
// Comment this line because import already handled by preprocessorOptions in vite.config.ts
// import "./styles/element-plus/index.scss";
import 'element-plus/dist/index.css'
// Tailwind
import './styles/tailwind/index.css'
import './styles/main.scss'

import App from './App.vue'
import router from './router'
import apiService from '@/utils/services/apiService'

import { abilitiesPlugin, ability } from './utils/plugins/casl'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(abilitiesPlugin, ability)
app.component('FontAwesomeIcon', FontAwesomeIcon)

const { showMessage } = useMessage()

// Add the request interceptor
apiService.interceptors.response.use(
  (response) => {
    // Success logic
    return response
  },
  (error) => {
    // Error handling

    // Show a message if the JWT token has expired
    if (error.response.data.errorCode === 'JWT_EXPIRED') {
      showMessage('error', 'Session Expired. Please login again.')
      router.replace({ name: 'login' })
    }

    return Promise.reject(error)
  }
)

const isProduction = import.meta.env.NODE_ENV === 'production'

if (import.meta.env.VITE_SENTRY_ENABLE === 'true') {
  const integrations = [
    Sentry.browserTracingIntegration({
      router // Vue Router tracing
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    // Add browser profiling integration to the list of integrations
    new Sentry.BrowserProfilingIntegration()
  ]

  if (import.meta.env.VITE_SENTRY_FEEDBACK_ENABLE === 'true') {
    integrations.push(
      Sentry.feedbackIntegration({
        // Additional SDK configuration goes in here, for example:
        colorScheme: 'system'
      })
    )
  }

  Sentry.init({
    app: app,
    environment: import.meta.env.VITE_NODE_ENV,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations,
    // Track Vue Components
    trackComponents: true,
    // Performance Monitoring
    tracesSampleRate: isProduction ? 0.1 : 1, //
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: isProduction ? 0.1 : 1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

// Handle preload error
window.addEventListener('vite:preloadError', () => {
  console.log('New content available. Refreshing the page.')

  window.location.reload() // Refresh the page
})

app.mount('#app')
